// Variable Node Component for Flow Builder

import React from 'react';
import { Handle, Position } from 'reactflow';
import {
  Box,
  Typography,
  Chip
} from '@mui/material';

interface VariableNodeProps {
  data: {
    label: string;
    description?: string;
    variableType?: string;
    dataType?: string;
    defaultValue?: string;
    status?: string;
    color?: string;
  };
  selected?: boolean;
}

const VariableNode: React.FC<VariableNodeProps> = ({ data, selected }) => {
  const { 
    label = 'Variable', 
    description = 'Store data values',
    variableType = 'Text',
    dataType = 'String',
    defaultValue = '',
    status = 'idle',
    color = '#FF7675'
  } = data;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return '#4CAF50';
      case 'error': return '#F44336';
      case 'updated': return '#FF9800';
      default: return '#9E9E9E';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'Text': return '📝';
      case 'Number': return '🔢';
      case 'Boolean': return '☑️';
      case 'Date': return '📅';
      case 'Collection': return '📚';
      case 'Record': return '📄';
      default: return '📊';
    }
  };

  return (
    <Box
      sx={{
        minWidth: 180,
        maxWidth: 280,
        background: 'linear-gradient(135deg, rgba(255, 118, 117, 0.1) 0%, rgba(255, 118, 117, 0.2) 100%)',
        border: selected ? `3px solid ${color}` : `2px solid ${color}`,
        borderRadius: '12px',
        padding: 2,
        boxShadow: selected 
          ? `0 8px 25px rgba(255, 118, 117, 0.4)` 
          : '0 4px 12px rgba(0,0,0,0.1)',
        transition: 'all 0.2s ease',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          boxShadow: '0 8px 25px rgba(255, 118, 117, 0.3)',
          transform: 'translateY(-2px)'
        }
      }}
    >
      {/* Top decoration */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: `linear-gradient(90deg, ${color} 0%, rgba(255, 118, 117, 0.6) 100%)`
        }}
      />

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            backgroundColor: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.2rem'
          }}
        >
          {getTypeIcon(variableType)}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>
            {label}
          </Typography>
          <Typography variant="caption" sx={{ color: '#666', lineHeight: 1 }}>
            {variableType} Variable
          </Typography>
        </Box>
        <Chip
          size="small"
          label={status}
          sx={{
            backgroundColor: getStatusColor(status),
            color: 'white',
            fontSize: '0.7rem',
            height: 20
          }}
        />
      </Box>

      {/* Description */}
      <Typography 
        variant="body2" 
        sx={{ 
          color: '#555', 
          mb: 1.5, 
          fontSize: '0.8rem',
          lineHeight: 1.3
        }}
      >
        {description}
      </Typography>

      {/* Variable Details */}
      <Box
        sx={{
          background: 'rgba(255,255,255,0.8)',
          border: '1px solid rgba(255, 118, 117, 0.3)',
          borderRadius: '8px',
          p: 1.5
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="caption" sx={{ color: '#666', fontWeight: 'bold' }}>
            Data Type:
          </Typography>
          <Typography variant="caption" sx={{ color: '#333' }}>
            {dataType}
          </Typography>
        </Box>
        
        {defaultValue && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ color: '#666', fontWeight: 'bold' }}>
              Default:
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                color: '#333',
                maxWidth: '100px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {defaultValue}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Connection Handles */}
      <Handle
        type="source"
        position={Position.Right}
        style={{
          background: color,
          border: '2px solid white',
          width: 12,
          height: 12
        }}
      />
    </Box>
  );
};

export default VariableNode;